export const CONTACT_EMAIL = "support@mileagetaxapp.com";
const EFFECTIVE = "October 7, 2026";

export const PRIVACY = {
  id: "privacy",
  title: "Privacy Policy",
  effective: EFFECTIVE,
  intro:
    "This Privacy Policy explains what information MileageTax (\"MileageTax,\" \"we,\" \"us\") collects when you use the MileageTax website and app (the \"Service\"), how we use it, and the choices you have. MileageTax is a U.S.-based mileage and business-expense recordkeeping service.",
  sections: [
    {
      h: "1. Information you give us",
      body: [
        "We collect only the information you enter into the Service or that is needed to run it:",
        [
          "Account information: your email address, your name (optional), and your password. Passwords are stored only as a one-way hash, never in readable form.",
          "Business profile: your name, business name, business type, selected tax year, and any custom mileage rates you set.",
          "Mileage records: trip date, starting and ending odometer readings, calculated miles, whether the trip is business or personal, and any purpose, client/job, and notes you add.",
          "Expense records: date, amount, category, and description.",
          "Receipts: image or PDF files you upload (up to 10 MB each), along with the original file name, file type, and file size.",
        ],
      ],
    },
    {
      h: "2. Payment information",
      body: [
        "Subscriptions are processed by Stripe, Inc. When you subscribe, you enter your payment details on Stripe's secure checkout page. Stripe collects and processes your card details, billing email, and billing address under its own privacy policy (stripe.com/privacy). MileageTax does not receive or store your full card number.",
        "From Stripe we store only your Stripe customer ID, subscription ID, subscription status, current billing-period end date, and whether your subscription is set to cancel at the end of the period. We use this to determine whether your account has access to paid features.",
      ],
    },
    {
      h: "3. Information collected automatically",
      body: [
        [
          "Sign-in cookies and storage: when you log in, we set secure, HTTP-only cookies and store sign-in tokens in your browser's local storage to keep you logged in. These are used only for authentication.",
          "Security records: we keep a count of recent failed login attempts for each account email to temporarily lock the account after repeated failures, and we keep password-reset tokens (stored in hashed form) that expire after 60 minutes.",
          "Usage analytics and session recording: the Service's web pages load an analytics tool (PostHog) provided through our hosting platform. It collects usage information such as pages viewed, clicks and interactions, browser and device type, approximate location derived from IP address, and session recordings of how pages are used. This information is used to operate, troubleshoot, and improve the Service.",
        ],
        "We do not use advertising cookies, and the Service does not show ads.",
      ],
    },
    {
      h: "4. How we use information",
      body: [
        [
          "To provide the Service: store your trips, expenses, and receipts; calculate miles and estimated deductions; and generate your reports and exports.",
          "To manage your account and subscription, including checking your subscription status with Stripe.",
          "To send service emails, such as password-reset links. We do not send marketing emails.",
          "To protect the Service and your account, including limiting repeated failed logins.",
          "To understand how the Service is used and fix problems.",
          "To comply with legal obligations.",
        ],
      ],
    },
    {
      h: "5. How information is shared",
      body: [
        "We do not sell your personal information, and we do not share it for advertising. Your records are private to your account; other users cannot see them. We share information only as follows:",
        [
          "Service providers: our hosting platform (Emergent), which provides the servers, database, file storage for receipts, the email service used for password-reset emails, and the analytics tool described above; and Stripe, for payment processing.",
          "Legal reasons: if required by law, subpoena, or other legal process, or to protect the rights, safety, or property of MileageTax, our users, or others.",
          "Business transfers: if MileageTax is involved in a merger, sale, or transfer of assets, your information may be transferred as part of that transaction, subject to this Policy.",
        ],
      ],
    },
    {
      h: "6. Data retention and deletion",
      body: [
        "We keep your account information and records for as long as your account exists. When you delete a trip or expense in the app, it is removed from your records. When you remove or replace a receipt, it is removed from your account, but the underlying file may remain in our file storage until your account data is deleted at your request.",
        `To delete your account and associated data, email us at ${CONTACT_EMAIL} from the email address on your account. We may retain limited information where required by law or to resolve disputes, and Stripe retains payment records under its own policies.`,
      ],
    },
    {
      h: "7. Security",
      body: [
        "We use reasonable measures to protect your information, including encrypted (HTTPS) connections, hashed passwords, per-account access controls, and temporary account lockout after repeated failed login attempts. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
      ],
    },
    {
      h: "8. Your choices and rights",
      body: [
        [
          "Access and correction: you can view and edit your profile, trips, and expenses in the app at any time. Subscribers can also download their records as CSV files and a PDF tax summary.",
          `Deletion: you can request deletion of your account and data by emailing ${CONTACT_EMAIL}.`,
          "Cookies: you can clear cookies and local storage in your browser, but you will be logged out and will need to log in again.",
        ],
        "California residents: depending on applicable law, you may have the right to know what personal information we collect, request access to or deletion of it, request correction, and not be discriminated against for exercising these rights. We do not sell personal information or share it for cross-context behavioral advertising. To make a request, email us; we may need to verify your identity before responding.",
        "Do Not Track: the Service does not currently respond to browser \"Do Not Track\" signals.",
      ],
    },
    {
      h: "9. Children",
      body: ["The Service is intended for adults running a business and is not directed to children under 18. We do not knowingly collect information from children."],
    },
    {
      h: "10. Changes to this Policy",
      body: ["We may update this Privacy Policy from time to time. If we make material changes, we will update the effective date above and, where appropriate, notify you through the Service or by email."],
    },
    {
      h: "11. Contact us",
      body: [`Questions or requests about this Privacy Policy can be sent to ${CONTACT_EMAIL}.`],
    },
  ],
};

export const TERMS = {
  id: "terms",
  title: "Terms of Service",
  effective: EFFECTIVE,
  intro:
    "These Terms of Service (\"Terms\") govern your use of the MileageTax website and app (the \"Service\"), operated by MileageTax (\"MileageTax,\" \"we,\" \"us\"). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.",
  sections: [
    {
      h: "1. The Service",
      body: [
        "MileageTax is a U.S.-based recordkeeping tool that helps self-employed people and small businesses log business mileage, track business expenses, store receipts, and view estimated mileage deductions and expense totals.",
      ],
    },
    {
      h: "2. Not tax, legal, or accounting advice",
      body: [
        "MileageTax provides recordkeeping tools and estimated tax deductions only. MileageTax does not provide tax, legal, or accounting advice, and nothing in the Service is a substitute for advice from a qualified professional.",
        [
          "Deduction amounts shown in the Service are estimates based on the records you enter and the mileage rates in your settings. They are not a determination of what you can deduct or what you owe.",
          "Standard IRS mileage rates are included for convenience and may change. You are responsible for confirming current official rates at irs.gov and for any custom rates you enter.",
          "You are solely responsible for the accuracy and completeness of your records, your tax filings, and your compliance with tax laws. Consult a qualified tax professional before relying on any figure from the Service.",
        ],
      ],
    },
    {
      h: "3. Eligibility and accounts",
      body: [
        [
          "You must be at least 18 years old and able to form a binding contract to use the Service.",
          "You agree to provide an accurate email address and keep your login credentials confidential. You are responsible for all activity under your account.",
          `Notify us promptly at ${CONTACT_EMAIL} if you believe your account has been accessed without authorization.`,
        ],
      ],
    },
    {
      h: "4. Free access and subscription",
      body: [
        "Without a subscription, you can create an account and use the Dashboard and Settings. Logging trips, recording expenses, uploading receipts, viewing tax reports, and exporting records require an active MileageTax subscription.",
        [
          "Price: the subscription costs $9.99 (U.S. dollars) per month.",
          "Recurring billing: by subscribing, you authorize us, through our payment processor Stripe, to charge your payment method $9.99 at the start of your subscription and automatically every month after that until you cancel.",
          "Payment processing: payments are processed by Stripe and are subject to Stripe's terms. We do not store your full card details.",
          "Failed payments: if a payment fails or your subscription becomes inactive, access to paid features may be suspended until a successful payment is made. Your existing records are not deleted because of a failed payment.",
          "Price changes: we may change the subscription price in the future. We will notify you in advance, and the new price will apply starting with your next billing period after the notice. If you do not agree, you may cancel before the change takes effect.",
        ],
      ],
    },
    {
      h: "5. Cancellation and refunds",
      body: [
        `You may cancel your subscription at any time by clicking "Manage Subscription" in Settings and completing cancellation through the Stripe customer portal, or by emailing ${CONTACT_EMAIL} from the email address on your account. Cancellation stops future renewals. Your subscription will remain active, and you will keep access to paid features, until the end of your current paid billing period.`,
        "Except where required by law, payments are non-refundable, and we do not provide refunds or credits for partial months or unused time.",
        "After your subscription ends, your account and records remain available under free access (Dashboard and Settings) unless you request deletion.",
      ],
    },
    {
      h: "6. Your data",
      body: [
        "You keep ownership of the trips, expenses, receipts, and other information you add to the Service (\"Your Content\"). You grant us a limited permission to store, process, and display Your Content solely to provide and improve the Service for you. Our handling of personal information is described in our Privacy Policy.",
        "You are responsible for keeping your own copies of records you need for tax or legal purposes. Subscribers can download records as CSV files and a PDF tax summary.",
      ],
    },
    {
      h: "7. Acceptable use",
      body: [
        "You agree not to:",
        [
          "use the Service for any unlawful purpose, including to create false or fraudulent records or to evade taxes;",
          "upload content you do not have the right to upload, or files containing malware or harmful code;",
          "access or attempt to access another user's account or data;",
          "probe, scan, or test the vulnerability of the Service, or bypass its security, access controls, or subscription requirements;",
          "interfere with or overload the Service, or use automated tools to access it except through normal use;",
          "resell, sublicense, or commercially exploit the Service without our written permission.",
        ],
      ],
    },
    {
      h: "8. Third-party services",
      body: ["The Service relies on third-party providers, including Stripe for payments and our hosting platform for servers, storage, and email. We are not responsible for third-party services, and your use of them may be subject to their own terms."],
    },
    {
      h: "9. Suspension and termination",
      body: [
        "We may suspend or terminate your access if you violate these Terms or if needed to protect the Service or other users. You may stop using the Service at any time and may request deletion of your account by emailing us. Sections that by their nature should survive termination (including Sections 2, 6, 10, 11, 12, and 13) will survive.",
      ],
    },
    {
      h: "10. Disclaimers",
      body: [
        "THE SERVICE IS PROVIDED \"AS IS\" AND \"AS AVAILABLE,\" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, THAT DATA WILL NEVER BE LOST, OR THAT ANY CALCULATION OR ESTIMATE WILL BE ACCURATE OR ACCEPTED BY ANY TAX AUTHORITY.",
      ],
    },
    {
      h: "11. Limitation of liability",
      body: [
        "TO THE FULLEST EXTENT PERMITTED BY LAW, MILEAGETAX WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, DATA, OR TAX BENEFITS, OR FOR ANY TAXES, PENALTIES, OR INTEREST, ARISING FROM OR RELATED TO YOUR USE OF THE SERVICE.",
        "TO THE FULLEST EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM OR RELATED TO THE SERVICE WILL NOT EXCEED THE AMOUNT YOU PAID US FOR THE SERVICE IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM.",
        "Some jurisdictions do not allow certain limitations, so some of the above may not apply to you.",
      ],
    },
    {
      h: "12. Indemnification",
      body: ["You agree to indemnify and hold harmless MileageTax from any claims, losses, and expenses (including reasonable attorneys' fees) arising from your use of the Service, Your Content, or your violation of these Terms or the law."],
    },
    {
      h: "13. Governing law",
      body: ["These Terms are governed by the laws of the State of California, without regard to its conflict-of-laws rules. Any dispute arising from these Terms or the Service will be brought in the state or federal courts located in California, and you and MileageTax consent to the jurisdiction of those courts."],
    },
    {
      h: "14. Changes to these Terms",
      body: ["We may update these Terms from time to time. If we make material changes, we will update the effective date above and, where appropriate, notify you through the Service or by email. Continued use of the Service after changes take effect means you accept the updated Terms."],
    },
    {
      h: "15. Contact us",
      body: [`Questions about these Terms can be sent to ${CONTACT_EMAIL}.`],
    },
  ],
};
